"use client"

import * as React from "react"
import { IconChevronRight, type Icon } from "@tabler/icons-react"
import Link from "next/link"
import { usePathname } from "next/navigation"

import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

// Same row treatment as the main rail (see ROW in nav-main.tsx) — the footer
// block (Notifications / Users / Teams / Activity log / Settings) reads as a
// continuation of the nav, separated only by the footer hairline.
const ROW =
  "gap-2.5 rounded-lg px-2.5 text-[13px] text-sidebar-foreground/75 " +
  "hover:bg-surface-3 hover:text-sidebar-foreground active:bg-surface-3 active:text-sidebar-foreground " +
  "data-[active=true]:bg-card data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground data-[active=true]:shadow-xs " +
  "dark:data-[active=true]:bg-surface-3 dark:data-[active=true]:shadow-none " +
  "[&>svg]:text-text-dim data-[active=true]:[&>svg]:text-sidebar-primary"

export interface NavSecondaryItem {
  title: string
  /** Absent on flyout parents (they navigate via their children). */
  url?: string
  icon: Icon
  iconColor?: string
  badge?: number
  /**
   * When present the row renders as a flyout trigger (right-side dropdown)
   * instead of a link — used to fold the admin pages into one
   * "Administration" row so the footer stays short on laptop viewports.
   */
  items?: { title: string; url: string; icon: Icon }[]
}

export function NavSecondary({
  items,
  ...props
}: {
  items: NavSecondaryItem[]
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const pathname = usePathname()
  const isActive = (url: string) =>
    pathname === url || pathname.startsWith(url + "/")
  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu className="gap-px">
          {items.map((item) => {
            if (item.items) {
              return (
                <SidebarMenuItem key={item.title}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={item.items.some((child) => isActive(child.url))}
                        className={cn(ROW, "py-1.5")}
                      >
                        <item.icon />
                        <span className="group-data-[collapsible=icon]:hidden">
                          {item.title}
                        </span>
                        <IconChevronRight className="ml-auto size-3.5 group-data-[collapsible=icon]:hidden" />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="end" className="w-52">
                      {item.items.map((child) => (
                        <DropdownMenuItem key={child.url} asChild>
                          <Link
                            href={child.url}
                            className={cn(
                              isActive(child.url) && "bg-accent font-medium"
                            )}
                          >
                            <child.icon className="mr-2 size-4 text-text-dim" />
                            {child.title}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              )
            }
            const active = isActive(item.url!)
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={active}
                  className={cn(ROW, "py-1.5")}
                >
                  <Link href={item.url!}>
                    <item.icon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      {item.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
                {item.badge !== undefined && item.badge > 0 && (
                  <SidebarMenuBadge className="bg-destructive rounded-full text-[11px] font-semibold text-white tabular-nums">
                    {item.badge > 99 ? '99+' : item.badge}
                  </SidebarMenuBadge>
                )}
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
